// @angular/*
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

//msal
import { MsalModule, MsalRedirectComponent, MsalInterceptor, MsalService, MsalGuard, MsalBroadcastService, MSAL_INSTANCE } from '@azure/msal-angular';
import { loginRequest } from './msal.config';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { MSAL_ENTRA_INSTANCE, MSAL_B2C_INSTANCE, MSALEntraInstanceFactory, MSALB2CInstanceFactory } from './msal-instances';
import { initializeMsalInstances } from './msal.initializer';
import { MsalInitializerService } from './_shared/services/authentication/msal-initializer.service';

// Services
import { AuthGuard } from './_shared/route-guards/authGuard';

import { AuthenticationInterceptorService } from './_shared/services/authentication/interceptors/authenticationInterceptor.service';
import * as Services from './_shared/services/services-index';
import * as APIService from './_shared/services/api/api-index';

// Core modules
import { BrowserModule } from '@angular/platform-browser';
import { FlexLayoutModule } from '@angular/flex-layout';
import { Injector, NgModule, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToasterModule } from '@dkachko/angular2-toaster';
import { MonacoEditorModule, MONACO_PATH } from '@materia-ui/ngx-monaco-editor';
// import { ClickOutsideModule } from 'ng-click-outside';
import { AppComponent } from './app.component';
import { GlobalErrorHandler } from './global-error-handler';

// environment
import { environment } from '../environments/environment';

// ngrx
import { StoreModule, MetaReducer, META_REDUCERS } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { AppEffects } from './_store/app.effects';
import { StoreRouterConnectingModule, routerReducer, MinimalRouterStateSerializer, NavigationActionTiming } from '@ngrx/router-store';
import { authReducer, configReducer, localeReducer, logger } from './_store/app.reducer';
import * as fromFilter from './_shared/filter/store';
import * as fromReportViews from './_shared/report-view/store';
import { ROOT_STORAGE_KEYS, ROOT_LOCAL_STORAGE_KEY } from './_store/app.tokens';

// shared module - loads of treats
import { SharedModule } from './_shared/shared.module';

import { SetAppInjector } from './app.injector';
import { AppRouter } from './app.routes';
import { HttpErrorInterceptor } from './http-error.interceptor';

// Layout
import * as Layout from './_layout/layout-index';

// Modals
import * as Modals from './_shared/modals/modals-index';

// Pages
import { ExceptionComponent } from './errors/exception.component';
import { LoginV2Component } from './login/login-v2.component';
import { ImpersonateComponent } from './login/impersonate.component';
import { EntraCallbackComponent } from './login/entra-callback.component';
import { SsoLoginV5Component } from './login/ssologinV5.component';
import { SloLogoutComponent } from './logout/slologout.component';
import { NotFoundComponent } from './errors/404.component';
import { ErrorsComponent } from './errors/errors.component';
import { B2CCallbackComponent } from './login/b2c-callback.component';

import { BaseComponent } from './_shared/components/base.component';


// Genius template
import { SIDEBAR_TOGGLE_DIRECTIVES } from './_shared/utilities/sidebar.directive';

// Directives
import * as Directives from './_shared/directives/directives-index';
import { storageMetaReducer } from './_store/storage.metareducer';
import { LogoutComponent } from './logout/logout.component';
import { ReportViewFiltersService } from './_shared/services/services-index';
// Use the original factory for compatibility with existing code
import { MSALInstanceFactory } from './msal-instance.factory';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { DynamicRoutingService } from './_shared/services/config/dynamic-routing.service';

export function breadcrumbServiceFactory(router: Router) {
  return new Services.BreadcrumbService(router);
}

// factory meta-reducer configuration function
export function getAppStorageMetaReducer(saveKeys: string[],
  localStorageKey: string,
  storageService: Services.LocalStorageService,
  reportViewFiltersService: ReportViewFiltersService,
  router: Router
): MetaReducer<any> {
  return storageMetaReducer(saveKeys, localStorageKey, storageService, reportViewFiltersService, router);
}

@NgModule({
    bootstrap: [
        AppComponent,
        MsalRedirectComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FlexLayoutModule,
        FormsModule,
        ReactiveFormsModule,
        ToasterModule.forRoot(),
        AppRouter,
        SharedModule,
        MonacoEditorModule,
        StoreModule.forRoot({
            auth: authReducer,
            config: configReducer,
            filters: fromFilter.filterReducer,
            locale: localeReducer,
            reportViews: fromReportViews.reportViewReducer,
            router: routerReducer }, {
            metaReducers: [logger],
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true,
                strictStateSerializability: false,
                strictActionSerializability: false
            }
        }),
        // StoreModule.forFeature('reportViews', fromReportViews.reportViewReducer),
        // StoreModule.forFeature('filters', fromFilter.filterReducer),
        StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production , connectInZone: true}),
        EffectsModule.forRoot([AppEffects, fromFilter.FilterEffects, fromReportViews.ReportViewEffects]),
        StoreRouterConnectingModule.forRoot({
            navigationActionTiming: NavigationActionTiming.PostActivation,
            serializer: MinimalRouterStateSerializer
        }),
        // configure msal - used for MSAL guards and interceptors (if needed)
        // This is just a placeholder instance, our actual authentication will use the
        // specific instances injected with MSAL_ENTRA_INSTANCE and MSAL_B2C_INSTANCE
        MsalModule.forRoot(
           new PublicClientApplication({ 
               auth: { 
                   clientId: environment.production ? '' : 'placeholder-client-id',
                   authority: 'https://login.microsoftonline.com/common'
               }
            }),
            {
                interactionType: InteractionType.Redirect,
                authRequest: loginRequest
            },
            {
                interactionType: InteractionType.Redirect,
                protectedResourceMap: new Map()
            }
        )
    ],
    declarations: [
        AppComponent,
        // MenuItemComponent,
        Directives.NavDropdownDirective,
        Directives.NavDropdownToggleDirective,
        Directives.ItemSelectedToggleDirective,

    // layout
    Layout.LayoutComponent,
    Layout.BreadcrumbComponent,
    Layout.LayoutFooterComponent,
    Layout.LayoutSidebarComponent,
    // Layout.LayoutSecondaryNavComponent,
    Layout.LayoutNavbarComponent,
    Layout.LanguageSelectorModalComponent,
    Layout.NavbarUserComponent,
    Layout.NavbarNotificationsComponent,
    Layout.NavbarLogoutComponent,
    Layout.SdMetricSelectorComponent,
    Layout.SupportModalComponent,
    Layout.SpinnerComponent,
    Layout.PerspectiveSelectorComponent,

    // Modals
    Modals.GenericModalComponent,
    Modals.SharedHelpTextModalComponent,

        // Translations
        // TranslateLayoutPipe,
        // pages
        ExceptionComponent,
        NotFoundComponent,
        LoginV2Component,
        ImpersonateComponent,
        EntraCallbackComponent,
        B2CCallbackComponent,
        LogoutComponent,
        SsoLoginV5Component,
        SloLogoutComponent,
        ErrorsComponent,

    BaseComponent,

    // template
    SIDEBAR_TOGGLE_DIRECTIVES
  ],
  providers: [
    HttpClient,
    Services.AuthenticationService,
    Services.RuntimeConfigService,
    Services.RoutingConfigService,
    Services.BillboardService,
    Services.ConfigurationService,
    Services.DataTableService,
    Services.LocalCacheService,
    Services.RemoteCacheService,
    Services.AppInsightsService,
    Services.LoggerService,
    Services.FilterService,
    Services.SpinnerService,
    Services.UtilsService,
    Services.ExportsService,
    Services.SdDateRangePickerService,
    Services.SdMetricSelectorService,
    Services.OrgLookupService,
    Services.LocaleService,
    Services.LayoutTranslationService,
    Services.ResourceService,
    Services.HelpTextService,
    // API Services
    APIService.ContentServiceV2,
    APIService.LeadTransactionService,
    APIService.ReportsService,
    APIService.UsersService,
    // Helper Services
    Layout.LayoutHelperService,
    AuthGuard,
    // Initialize dynamic routes from API-driven config
    provideAppInitializer(() => {
      const dynamicRoutingService = inject(DynamicRoutingService);
      console.log('APP_INITIALIZER: Setting up dynamic routes');
      return dynamicRoutingService.setupRoutes();
    }),
    provideAppInitializer(
      () => initializeMsalInstances(
        inject(MsalInitializerService),
        inject(Services.RuntimeConfigService)
      )()
    ),
    // Main MSAL_INSTANCE for Angular MSAL module compatibility
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [Services.RuntimeConfigService]
    },
    // Dedicated Entra ID MSAL instance
    {
      provide: MSAL_ENTRA_INSTANCE,
      useFactory: MSALEntraInstanceFactory,
      deps: [Services.RuntimeConfigService]
    },
    // Dedicated B2C MSAL instance
    {
      provide: MSAL_B2C_INSTANCE,
      useFactory: MSALB2CInstanceFactory,
      deps: [Services.RuntimeConfigService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationInterceptorService,
      multi: true
    },
    {

      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },
    {
      provide: Services.BreadcrumbService,
      useFactory: breadcrumbServiceFactory,
      deps: [Router]
    },
    {
      provide: 'moment', useFactory: (): any => moment
    },
    { provide: 'Window', useValue: window },
    { provide: ROOT_STORAGE_KEYS, useValue: ['filters', 'locale', 'reportViews'] },
    { provide: ROOT_LOCAL_STORAGE_KEY, useValue: '__app_storage__' },
    {
      provide: META_REDUCERS,
      deps: [ROOT_STORAGE_KEYS, ROOT_LOCAL_STORAGE_KEY, Services.LocalStorageService, Services.ReportViewFiltersService, Router],
      useFactory: getAppStorageMetaReducer,
      multi: true
    },
    {
      provide: MONACO_PATH,
      useValue: 'https://unpkg.com/monaco-editor@0.19.3/min/vs'
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // {
    //     provide: HTTP_INTERCEPTORS,
    //     useClass: MsalInterceptor,
    //     multi: true
    // }
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        overlayPanelClass: 'sd-full-width-select-panel',
      }
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
  constructor(injector: Injector) {
    SetAppInjector(injector);
  }
}
